<!-- sections/GroupOrderSection.vue -->
<script setup lang="ts">
import { watch, ref, onMounted, computed } from 'vue';
import BaseSection from '../components/BaseSection.vue';
import ConnxField, { ErrorUpdate } from '@/components/Molecules/Field/ConnxField.vue';
import { InputType } from '@/interfaces/InputFields';
import type { DashboardWidget, GroupOrOrderOption } from '@/interfaces/Dashboard';

interface Props {
  widget: DashboardWidget;
  groupBy?: GroupOrOrderOption;
  orderBy?: GroupOrOrderOption;
  errors?: Map<string, string | null>;
  showTitle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => new Map(),
  showTitle: true,
});

const emit = defineEmits<{
  'field-error': [{ name: string; error: string | null }];
}>();

const localGroupBy = ref(props.groupBy);
const localOrderBy = ref(props.orderBy);

// Filter order by options based on group by selection
const filterOrderByOptions = (groupByValue: string | null) => {
  if (!localOrderBy.value) return;

  const orderBy = localOrderBy.value;

  // Store fullValues on first run
  if (!orderBy.fullValues && orderBy.values) {
    orderBy.fullValues = [...orderBy.values];
  }

  if (orderBy.fullValues) {
    const allowedOptions = orderBy.fullValues.filter((opt: any) => !opt.visibleIf || opt.visibleIf === groupByValue);

    orderBy.values = allowedOptions;

    // Automatically select first option if current value is invalid
    if (!allowedOptions.find((opt: any) => opt.value === orderBy.value)) {
      if (allowedOptions.length > 0) {
        orderBy.value = allowedOptions[0].value;
      } else {
        orderBy.value = null;
      }
    }
  }
};

const handleFieldError = (error: ErrorUpdate) => {
  emit('field-error', error);
};

// Watch for groupBy changes
watch(
  () => localGroupBy.value?.value,
  (newGroupBy) => {
    filterOrderByOptions(newGroupBy);
  }
);

// Initialize on mount
onMounted(() => {
  if (localGroupBy.value?.value) {
    filterOrderByOptions(localGroupBy.value.value);
  }
});

const shouldShow = computed(() => !!localGroupBy.value || !!localOrderBy.value);
</script>

<template>
  <BaseSection :should-show="shouldShow" :show-title="showTitle">
    <div v-if="localGroupBy" class="tw-mb-4">
      <h5 class="Field__label tw-text-base">Group by</h5>
      <ConnxField
        name="groupBy"
        required
        :label="localGroupBy.label"
        v-model="localGroupBy.value"
        :type="InputType.Select"
        :options="localGroupBy.values"
        @update:error="handleFieldError"
      />
    </div>

    <div v-if="localOrderBy">
      <h5 class="Field__label tw-text-base">Order by</h5>
      <ConnxField
        name="orderBy"
        :label="localOrderBy.label"
        v-model="localOrderBy.value"
        :type="InputType.Select"
        :options="localOrderBy.values"
        @update:error="handleFieldError"
      />
    </div>
  </BaseSection>
</template>
